import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { getFeaturedPromoCardProperties } from '@/features/shared/analytics/promoCard'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { getBoxShadow } from '@/features/shared/styles/effects'
import { type HeroCardDisplayStyle, type HeroCardData } from '@/features/cms/components/CMS/types'
import { getPromotionFromData } from '@/features/cms/components/CMS/utils/promotion'
import { Badge } from '@/features/cms/components/CMS/components/Shelves/Badge'
import { useSelectPromoStore } from '@/features/cms/components/CMS/hooks/useSelectPromoStore'
import { StoreIcons } from '@/features/cms/components/CMS/components/Shelves/StoreIcons'
import { DescriptionText } from '@/features/cms/components/CMS/components/Shelves/LargePromoItem'
import { Body } from '@shipt/design-system-typography'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { Button } from '@shipt/design-system-buttons'

type Props = {
  heroCardData: HeroCardData
  index: number
  shelfIndex: number
  shelfName: string
}

/**
 * This component corresponds to the `hero_card` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/hero_card
 * - **Production** - https://cms.shipt.com/content-type/pool/hero_card
 */
export const HeroCard = ({ heroCardData, index, shelfIndex, shelfName }: Props) => {
  const { trackCMSPromoCardClicked } = useCMSTrackingEvents()
  const {
    data: {
      heading,
      sub_heading,
      display_style,
      image: { src: imgUrl },
      link: { text: buttonText, url: destinationUrl },
      available_stores,
      badge,
    },
  } = heroCardData

  const promotion = getPromotionFromData(heroCardData)

  const { selectPromoStore, isLoading, normalizedUrl, renderLinkInCard } = useSelectPromoStore(
    promotion,
    destinationUrl,
  )

  const handleOnClick = () => {
    trackCMSPromoCardClicked(
      getFeaturedPromoCardProperties({
        promotion,
        displayFormat: DISPLAY_FORMAT.HORIZONTAL,
        listIndex: index,
        shelfIndex,
        subLocation: shelfName,
        nextLocation: normalizedUrl,
      }),
    )

    selectPromoStore()
  }
  const { id } = promotion

  const buttonBody = isLoading ? (
    <LoadingIndicator size="md" />
  ) : (
    <ButtonText>{buttonText}</ButtonText>
  )

  return (
    <ContentContainer>
      {display_style !== 'Cropped' && <BackgroundImage alt="" src={imgUrl} fill sizes="360px" />}
      {!!badge && <Badge badge={badge} />}
      <HeadingAndCTAContainer displayStyle={display_style}>
        <div>
          <TitleText
            id={`promo_${id}_title`}
            variant="primary"
            size="xl"
            lineHeight="compact"
            strong
          >
            {heading}
          </TitleText>
          {!!sub_heading && (
            <DescriptionText size="md" lineHeight="compact" lineClamp={3}>
              {sub_heading}
            </DescriptionText>
          )}
        </div>
        {!!renderLinkInCard && (
          <ShopButtonLink
            size="md"
            href={normalizedUrl}
            onClick={(e) => {
              e.preventDefault()
              handleOnClick()
            }}
            aria-describedby={`promo_${id}_headline promo_${id}_title`}
          >
            {buttonBody}
          </ShopButtonLink>
        )}
        {!renderLinkInCard && (
          <ShopButton
            size="md"
            onClick={handleOnClick}
            aria-describedby={`promo_${id}_headline promo_${id}_title`}
          >
            {buttonBody}
          </ShopButton>
        )}
      </HeadingAndCTAContainer>
      {display_style === 'Cropped' && (
        <ImageWrapper>
          <ImageContainer>
            <Image src={imgUrl} alt={heading} width={180} height={180} preload={index === 0} />
          </ImageContainer>
        </ImageWrapper>
      )}
      <StoreIcons availableStores={available_stores} />
    </ContentContainer>
  )
}

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  width: 360px;
  height: 180px;
  border-radius: 0.5rem;
  ${getBoxShadow('rest', 'light')};
`

const BackgroundImage = styled(Image)`
  z-index: -1;
  object-fit: cover;
`

const ImageContainer = styled.div`
  position: relative;
  border-radius: 0.5rem;
  width: 180px;
`

const HeadingAndCTAContainer = styled.div<{
  displayStyle?: HeroCardDisplayStyle
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: ${({ displayStyle }) => (displayStyle !== 'Takeover' ? `flex-start` : 'center')};
  width: 100%;
  padding: 1rem;
  text-align: ${({ displayStyle }) => (displayStyle === 'Takeover' ? 'center' : 'left')};
`

const TitleText = styled(Body)`
  /* stylelint-disable-next-line value-no-vendor-prefix -- -webkit-line-clamp used here is only compatible with -webkit-box so we need to keep using it */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-bottom: 2px;
`

const ButtonText = styled.span`
  /* stylelint-disable-next-line value-no-vendor-prefix -- -webkit-line-clamp used here is only compatible with -webkit-box so we need to keep using it */
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const shopButtonStyles = css`
  max-width: 8.8rem;
  min-width: 6.5rem;
  padding: 0.375rem 1rem;
  margin-top: 0.375rem;
`
const ShopButtonLink = styled(ButtonLink)`
  ${shopButtonStyles}
`
const ShopButton = styled(Button)`
  ${shopButtonStyles}
`

const ImageWrapper = styled.div`
  width: 180px;
  height: 180px;
  overflow: hidden;
  border-radius: 0 0.5rem 0.5rem 0;
  flex-shrink: 0;

  & img {
    object-fit: contain;
    object-position: right center;
  }
`
